import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { ACTION_HEADER_INDENT_CLASS } from '../../../components/action-header'
import {
  AskUserQuestionCard,
  parseAskUserQuestionInput,
  type AskUserQuestionAnswer,
  type AskUserQuestionInput,
  type AskUserQuestionProgress,
} from '@renderer/chat/integrations/ask-user-question'
import { useAnswerQuestionAction } from '../../../hooks/use-answer-question-action'
import { useFloatingBarContentMaxHeight } from '../../../hooks/use-floating-bar-content-max-height'
import { ANSWERABLE_PENDING_CONTENT } from '@renderer/chat/integrations/answerable'
import { FLOATING_SURFACE_BORDER_CLASS, FLOATING_SURFACE_STYLE } from '@renderer/components/floating-surface'
import { BusyPill } from './busy-pill'
import { ActionArchiveCircle } from '../../../components/action-archive-circle'
import type { StagedAction } from '../../../hooks/use-staged-actions'

/**
 * Question-state card: normally floats above the chat input in place of the
 * Ready controls, and can use standard inset action-card chrome in a batched
 * output. Answering resolves the staged action via the `/answer` endpoint and
 * resumes the run that asked; an already answered question renders read-only.
 */
interface ActionViewQuestionCardProps {
  action: StagedAction
  /** Use the same inset body card as a standard staged action. */
  embedded?: boolean
  /**
   * Leave the interactive question to the floating bar while it is pending.
   * Once answered, the same card renders the settled answer in place.
   */
  deferPendingAnswer?: boolean
  progress?: AskUserQuestionProgress
  onProgress?: (answer: AskUserQuestionAnswer) => void
  /** The floating bar's dismissal, rendered in the card's footer. */
  onDismiss?: () => void
  /** Render only the contents when the host already provides the card surface. */
  contentOnly?: boolean
}

export function ActionViewQuestionCard({
  action,
  embedded = false,
  deferPendingAnswer = false,
  progress,
  onProgress,
  onDismiss,
  contentOnly = false,
}: ActionViewQuestionCardProps) {
  const { answerQuestion, isAnswering } = useAnswerQuestionAction()
  const floatingMaxHeight = useFloatingBarContentMaxHeight()

  const input = useMemo<AskUserQuestionInput | null>(
    () => parseAskUserQuestionInput(action.inputData),
    [action.inputData],
  )

  // A resolved question carries the answer in outputData; serialize it so the
  // card renders the prior selection read-only.
  const existingResult = useMemo<string | null>(() => {
    const out = action.outputData
    if (out && typeof out === 'object' && Array.isArray((out as { answers?: unknown }).answers)) {
      return JSON.stringify(out as AskUserQuestionAnswer)
    }
    return null
  }, [action.outputData])

  if (!input?.questions?.length) {
    // Nothing to answer, but the floating bar still needs a way to retire the action.
    return onDismiss ? <ActionArchiveCircle label="Dismiss" onSelect={onDismiss} /> : null
  }

  const isPending = !existingResult
  // Floating, the card is bounded by the panel and scrolls its body; the
  // hosted modes sit in a surface that already scrolls.
  const isFloating = !embedded && !contentOnly
  const content =
    deferPendingAnswer && isPending ? (
      <p className="text-foreground-secondary type-ui-sm">{ANSWERABLE_PENDING_CONTENT}</p>
    ) : (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        {/* While the answer posts, dim the options (kept mounted so a failure
          preserves the selection for retry) under the regenerating pill. */}
        {isAnswering && <BusyPill label="Applying your answer…" />}
        <div className={twMerge('flex min-h-0 flex-col', isAnswering && 'pointer-events-none opacity-40')}>
          <AskUserQuestionCard
            input={input}
            existingResult={existingResult}
            onSubmit={(answer) => answerQuestion(action.id, answer)}
            bare
            // Both hosted modes already title the card — the inset action-card
            // header (embedded) or the StagedActionCell header (contentOnly).
            showBareHeader={isFloating}
            scrollableBody={isFloating}
            progress={progress}
            onProgress={onProgress}
            onDismiss={onDismiss}
          />
        </div>
      </div>
    )

  if (contentOnly) {
    return content
  }

  if (embedded) {
    return (
      <div className={twMerge('pb-2', ACTION_HEADER_INDENT_CLASS)}>
        <div className="corner-smooth group-data-[focused]/staged:border-blue-9 group-data-[focused]/staged:ring-blue-9 relative overflow-hidden rounded-2xl border p-2.5 shadow-sm transition group-data-[focused]/staged:ring-1">
          {content}
        </div>
      </div>
    )
  }

  return (
    <div
      className={twMerge(
        FLOATING_SURFACE_BORDER_CLASS,
        'corner-smooth flex min-w-0 flex-1 flex-col gap-3 rounded-2xl px-3 pb-3 pt-2',
      )}
      style={{ ...FLOATING_SURFACE_STYLE, maxHeight: floatingMaxHeight }}
    >
      {content}
    </div>
  )
}
